import { GroupSecurity } from '@quromedical/fhir-common';
import type { AdmissionGetResponse } from '@quromedical/models';
import { first } from '@quromedical/utils';
import type { PatientRouteParams } from 'app/routes';
import { PatientHeaderSection } from 'core/patient';
import { Alert, SkeletonProvider, Snackbar, Tabs } from 'design-system';
import { useGlobalSearchParams } from 'expo-router';
import { useUserSession } from 'hooks/useUserSession';
import { AdmissionApi, admissionSWRKey } from 'integration/aggregate';
import type { PatientDrawerScreenProps } from 'navigation';
import type React from 'react';
import { useCallback } from 'react';
import { ScrollView } from 'react-native';
import { strings } from 'strings';
import useSWR from 'swr';

import { PatientMedicationsTab } from './tabs';

const admissionApi = new AdmissionApi();
export const PatientMedicationScreen: React.FC<
  PatientDrawerScreenProps<'Medication'>
> = () => {
  const { patientId } = useGlobalSearchParams<PatientRouteParams>();
  const session = useUserSession();

  const { data, isValidating, error, mutate } = useSWR(
    admissionSWRKey,
    async (): Promise<AdmissionGetResponse | undefined> => {
      const patientAdmissions = await admissionApi.getAdmissions({
        patient: patientId,
        status: 'in-progress',
      });
      const admission = first(patientAdmissions.admissions);

      return admission;
    },
  );

  const revalidate = useCallback(() => mutate, [mutate]);

  const admissionId = data?.id;

  const hasMedicationPermissions = session.hasAny([GroupSecurity.PatientRead]);

  if (error) {
    return (
      <Snackbar onClose={revalidate} isOpen={!!error}>
        <Alert
          backgroundColor="status-critical"
          textColor="white"
          onAction={revalidate}
          actionIcon="close"
          title={strings.ErrorCardTitle}
          body={strings.GenericErrorMessage}
        />
      </Snackbar>
    );
  }

  return (
    <ScrollView>
      <PatientHeaderSection id={patientId} />

      <SkeletonProvider loading={isValidating}>
        <Tabs.Group>
          <Tabs.Screen
            isHidden={!hasMedicationPermissions}
            title={strings.MedicationsCardTitle}
          >
            <PatientMedicationsTab
              patientId={patientId}
              admissionId={admissionId}
            />
          </Tabs.Screen>
        </Tabs.Group>
      </SkeletonProvider>
    </ScrollView>
  );
};
